/**
 * 시험용 진입 수단.
 *
 * 지형이나 수치를 손본 뒤 체감을 보려면 그 깊이의 전장에 바로 서 봐야 한다.
 * 그런데 깊이 4를 보려면 세 판을 이겨야 하고, 지형은 시드마다 달라서 숲이 낀
 * 전장이 나올 때까지 여러 번 돌려야 한다. 재려는 것보다 준비가 더 길다.
 *
 *   ?depth=4&seed=777&auto=1
 *
 * 결정론 덕에 같은 시드는 언제나 같은 지형과 같은 배치를 준다. 마음에 드는
 * 전장을 하나 찾으면 그 시드로 명령만 바꿔가며 비교할 수 있다. `npm run tactic`이
 * 하는 일을 손으로 하는 셈이다.
 *
 * **시뮬레이션 밖이다.** 읽는 것은 주소창뿐이고, 정해진 값은 평소와 똑같은
 * 경로로 `startRun`에 들어간다. 리플레이 규칙은 건드리지 않는다.
 */
import { CULTURE_NAMES, Culture, MAX_DEPTH } from '../sim/troops.ts';

/**
 * 시험 모드로 들어갈 수 있는 가장 깊은 곳. 게임의 가장 깊은 곳과 같다.
 *
 * 예전에는 `depthRoster`에 상한이 없어 여기서 따로 막았다. 명부가 `MAX_UNITS`(1400)를 넘으면
 * `spawn`이 조용히 실패하고, 스폰 순서가 아군 → 장수 → 동료 → 적 → **적장**이라 적장이 제일
 * 먼저 사라진다. "깊이 99"라고 쳐 놓고 적장 없는 판을 보게 된다. 이제 게임에도 끝이 있다.
 */
export const MAX_PLAYTEST_DEPTH = MAX_DEPTH;

export interface Playtest {
  /** 첫 전투를 이 깊이에서 시작한다. */
  readonly depth: number | null;
  /** 출정 시드를 고정한다. 같은 값이면 같은 지형과 같은 배치다. */
  readonly seed: number | null;
  /** 병영의 병력을 전부 싣고 곧장 출정한다. */
  readonly auto: boolean;
  /**
   * 이 나라의 새 계정으로 본다(`go`·`bj`·`sl`). 진영 선택(Todo9) 뒤로 백제·신라를 보려면 계정을 지우고 새로 골라야 해서
   * 넣었다. 시험 모드라 저장하지 않는다 — 키우던 계정은 그대로다.
   */
  readonly culture: Culture | null;
}

const NONE: Playtest = { depth: null, seed: null, auto: false, culture: null };

/**
 * 주소의 나라 글자 → 나라. 세 글자만 받는다 — 객체를 그대로 찾았더니 `?culture=constructor`·`__proto__`·`toString`도 무언가를
 * 돌려주어 `undefined_blade2` 병사를 만들고 화면이 멈췄다(리뷰가 짚었다).
 */
function cultureParam(raw: string | null): Culture | null {
  return raw === 'go' ? Culture.Goguryeo : raw === 'bj' ? Culture.Baekje : raw === 'sl' ? Culture.Silla : null;
}

function intParam(params: URLSearchParams, key: string, lo: number, hi: number): number | null {
  const raw = params.get(key);
  if (raw === null) return null;
  // 십진 숫자만 받는다. Number()에 맡기면 빈 문자열이 0이 되고 '0x10'이 16이
  // 되어, 치지도 않은 값이 조용히 들어간다. 여기로 들어온 값은 startRun까지 간다.
  if (!/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < lo || n > hi) return null;
  return n;
}

export function readPlaytest(search: string): Playtest {
  const params = new URLSearchParams(search);
  return {
    depth: intParam(params, 'depth', 1, MAX_PLAYTEST_DEPTH),
    seed: intParam(params, 'seed', 0, 0x7fffffff),
    auto: params.get('auto') === '1',
    culture: cultureParam(params.get('culture')),
  };
}

export function currentPlaytest(): Playtest {
  try {
    return readPlaytest(location.search);
  } catch {
    // 주소창을 못 읽는 환경이면 평소대로 간다.
    return NONE;
  }
}

/** 무엇이든 하나라도 걸려 있으면 시험 모드다. */
export function isPlaytest(p: Playtest): boolean {
  return p.depth !== null || p.seed !== null || p.auto || p.culture !== null;
}

/** 화면 구석에 지금 걸린 설정을 적는다. 켜 둔 줄 모르고 헤매지 않도록. */
export function playtestLabel(p: Playtest): string | null {
  const parts: string[] = [];
  if (p.depth !== null) parts.push(`깊이 ${p.depth}`);
  if (p.seed !== null) parts.push(`시드 ${p.seed}`);
  if (p.auto) parts.push('자동 출정 1회');
  if (p.culture !== null) parts.push(`${CULTURE_NAMES[p.culture]} 새 계정`);
  if (parts.length === 0) return null;
  // 계정을 건드리지 않는다는 것이 보여야 한다. 시험 판에서 병사가 죽었는데
  // 계정에 남는 줄 알고 놀라는 일이 없도록.
  return `시험 모드 · ${parts.join(' · ')} · 계정에 기록하지 않음`;
}
